
import React from 'react';
import {
    Box,
    Container,
    Typography,
    Stack,
    Grid,
    alpha,
    useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import SectionWrapper from '../components/SectionWrapper';

/**
 * GENESIS VISUAL: PARTNERS PAGE - "THE ARCHITECTURAL STREAM" (CYCLE 6 - RESTORED)
 * Concept: ANTI-CONTAINER. Pure Typography. High Contrast Rhythm.
 * Feedback: "nao ficou bom. volte a versao anterior a essa."
 */

const PartnersPage = () => {
    const navigate = useNavigate();
    const theme = useTheme();

    // Overview Page Style Variables
    const deepBlack = '#050505';
    const primaryRed = theme.palette.primary.main; // Usually #CC0000
    const textPrimary = '#FFFFFF';
    const textSecondary = alpha('#FFFFFF', 0.6);

    const partners = [
        {
            id: 'microsoft',
            name: 'MICROSOFT',
            tier: 'GOLD_NODE',
            desc: 'Potencializando a Transformação Digital com Azure e AI. Agentes cognitivos que transcendem a automação simples.',
            tech: ['AZURE', 'OPENAI', 'POWER_PLATFORM'],
        },
        {
            id: 'google',
            name: 'GOOGLE CLOUD',
            tier: 'PREMIER_NODE',
            desc: 'Inovação em Dados e AI com a infraestrutura do Google. Processamento massivo de tensores e modelagem preditiva avançada.',
            tech: ['VERTEX', 'GEMINI', 'BIGQUERY'],
        },
        {
            id: 'aws',
            name: 'AWS',
            tier: 'CORE_NODE',
            desc: 'Infraestrutura de escala global para missões críticas. A base sólida para aplicações que não podem falhar.',
            tech: ['LAMBDA', 'EC2', 'DYNAMO'],
        },
        {
            id: 'salesforce',
            name: 'SALESFORCE',
            tier: 'CRM_NODE',
            desc: 'Conectando empresas e clientes de forma inteligente. Conectando pontos de dados isolados em uma visão unificada.',
            tech: ['CRM', 'TABLEAU', 'SLACK'],
        }
    ];

    return (
        <SectionWrapper
            id="partners-page-v6-restored"
            sx={{
                bgcolor: deepBlack,
                color: textPrimary,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* AMBIENT LIGHTING */}
            <Box sx={{
                position: 'absolute', top: '-10%', right: '-30%',
                width: '80vw', height: '80vw',
                background: `radial-gradient(circle, ${alpha(primaryRed, 0.1)} 0%, transparent 60%)`,
                filter: 'blur(120px)',
                zIndex: 0,
                pointerEvents: 'none'
            }} />

            {/* HERO SECTION - COMPACT */}
            <Box sx={{
                pt: { xs: 16, md: 20 },
                pb: { xs: 8, md: 10 },
                position: 'relative',
                zIndex: 2
            }}>
                <Container maxWidth="xl">
                    <Typography
                        variant="overline"
                        sx={{
                            fontSize: '0.9rem',
                            color: textSecondary,
                            letterSpacing: '0.3em',
                            fontWeight: 600,
                            display: 'block',
                            mb: 2,
                            fontFamily: '"JetBrains Mono", monospace'
                        }}
                    >
                        04 // ALIANÇAS ESTRATÉGICAS
                    </Typography>
                </Container>
            </Box>

            {/* THE STREAM - LIST LAYOUT */}
            <Box sx={{ pb: 20, position: 'relative', zIndex: 2 }}>
                <Container maxWidth="xl">
                    <Stack spacing={0}>
	                        {partners.map((partner, index) => {
	                            // INTERCALATION LOGIC
	                            // Even indices (0, 2): White Name
	                            // Odd indices (1, 3): Red Name
	                            const isRed = index % 2 !== 0;
	                            const nameColor = isRed ? primaryRed : textPrimary;
	                            const textAlign = isRed ? 'right' : 'left';

	                            return (
                                <Box
                                    key={partner.id}
                                    onClick={() => navigate(`/parceiros/${partner.id}`)}
                                    sx={{
                                        py: { xs: 8, md: 10 },
                                        borderTop: `1px solid ${alpha('#FFF', 0.1)}`,
                                        cursor: 'pointer',
                                        transition: 'all 0.5s cubic-bezier(0.25, 1, 0.5, 1)',
                                        '&:hover': {
                                            bgcolor: alpha('#FFF', 0.02),
                                            '& .partner-arrow': { opacity: 1, transform: 'translateX(0)' },
                                            '& .partner-name': { transform: 'translateX(10px)' }
                                        }
                                    }}
                                >
                                    <Grid container spacing={4} alignItems="center" direction={isRed ? 'row-reverse' : 'row'}>

                                        {/* NAME BLOCK */}
                                        <Grid item xs={12} md={7}>
                                            <Stack alignItems={isRed ? 'flex-end' : 'flex-start'}>
                                                <Typography variant="overline" sx={{
                                                    color: textSecondary,
                                                    letterSpacing: '0.2em',
                                                    mb: 1,
                                                    display: 'block'
                                                }}>
                                                    [{partner.tier}]
                                                </Typography>

                                                <Typography
                                                    className="partner-name"
                                                    variant="h1"
                                                    sx={{
                                                        fontWeight: 800,
                                                        fontSize: { xs: '3rem', md: '6rem', lg: '8rem' },
                                                        lineHeight: 0.9,
                                                        letterSpacing: '-0.04em',
                                                        color: nameColor,
                                                        textTransform: 'uppercase',
                                                        transition: 'transform 0.5s ease',
                                                        mb: 2,
                                                        textAlign: textAlign
                                                    }}
                                                >
                                                    {partner.name}
                                                </Typography>

                                                <Box
                                                    className="partner-arrow"
                                                    sx={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        color: nameColor,
                                                        opacity: { xs: 1, md: 0 },
                                                        transform: { xs: 'none', md: 'translateX(-20px)' },
                                                        transition: 'all 0.4s ease'
                                                    }}
                                                >
                                                    <Typography variant="button" sx={{ mr: 1, letterSpacing: '0.1em' }}>EXPLORAR</Typography>
                                                    <ArrowForwardIcon />
                                                </Box>
                                            </Stack>
                                        </Grid>

                                        {/* INFO BLOCK */}
                                        <Grid item xs={12} md={5}>
                                            <Stack spacing={4} alignItems={isRed ? 'flex-end' : 'flex-start'} sx={{ textAlign: textAlign }}>

                                                <Typography variant="h5" sx={{
                                                    color: '#E0E0E0',
                                                    fontWeight: 300,
                                                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                                                    maxWidth: '500px',
                                                    lineHeight: 1.4
                                                }}>
                                                    {partner.desc}
                                                </Typography>

                                                <Stack direction="row" spacing={2} justifyContent={isRed ? 'flex-end' : 'flex-start'}>
                                                    {partner.tech.map((t) => (
                                                        <Typography key={t} variant="caption" sx={{
                                                            fontFamily: '"JetBrains Mono", monospace',
                                                            color: textSecondary,
                                                            border: `1px solid ${alpha(textSecondary, 0.3)}`,
                                                            px: 1.5, py: 0.5,
                                                            fontSize: '0.75rem'
                                                        }}>
                                                            {t}
                                                        </Typography>
                                                    ))}
                                                </Stack>

                                            </Stack>
                                        </Grid>

                                    </Grid>
                                </Box>
                            );
                        })}
                        {/* Final Divider */}
                        <Box sx={{ borderTop: `1px solid ${alpha('#FFF', 0.1)}` }} />
                    </Stack>
                </Container>
            </Box>
        </SectionWrapper>
    );
};

export default PartnersPage;
